// Firebase Google OAuth backed by modular Firebase Auth and Firestore.
// Auth persists the session; Firestore owns the client/provider profile data.

console.log('[Firebase OAuth] Loading...');

const FIREBASE_SDK_VERSION = '12.18.0';
let firebaseModulesPromise = null;

function notify(message, type) {
  if (typeof window.showNotification === 'function') {
    window.showNotification(message, type);
  }
}

async function getFirebaseServices() {
  const ready = await window.ensureFirebaseReady?.();
  const auth = window.firebaseAuthRaw;
  const db = window.firebaseDbRaw;
  const provider = window.firebaseGoogleProvider;

  if (!ready || !auth || !db || !provider) {
    throw new Error('Firebase Auth and Firestore are not ready.');
  }

  return { auth, db, provider };
}

function getFirebaseModules() {
  if (!firebaseModulesPromise) {
    firebaseModulesPromise = Promise.all([
      import(`https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-auth.js`),
      import(`https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-firestore.js`)
    ]).then(([auth, firestore]) => ({ auth, firestore }));
  }
  return firebaseModulesPromise;
}

async function signOutSafely(auth) {
  const { auth: authModule } = await getFirebaseModules();
  await authModule.signOut(auth);
}

async function assertNotAdmin(user) {
  const token = await user.getIdTokenResult(true);
  const claims = token.claims || {};
  if (claims.admin === true || claims.role === 'admin') {
    throw new Error('Super Admin must use the Super Admin login form.');
  }
}

function clientProfile(user, existing) {
  return {
    ...(existing || {}),
    id: user.uid,
    uid: user.uid,
    email: (user.email || '').toLowerCase(),
    name: existing?.name || user.displayName || (user.email || '').split('@')[0],
    phone: existing?.phone || user.phoneNumber || '',
    country: existing?.country || '',
    role: 'client',
    subscriptionPlan: existing?.subscriptionPlan || 'free',
    subscriptionStatus: existing?.subscriptionStatus || 'active'
  };
}

async function loadOrCreateClient(db, user) {
  const { firestore } = await getFirebaseModules();
  const ref = firestore.doc(db, 'clients', user.uid);
  const snapshot = await firestore.getDoc(ref);
  const profile = clientProfile(user, snapshot.exists() ? snapshot.data() : null);

  if (snapshot.exists()) {
    await firestore.setDoc(ref, {
      id: profile.id,
      uid: profile.uid,
      email: profile.email,
      name: profile.name,
      role: 'client'
    }, { merge: true });
  } else {
    await firestore.setDoc(ref, {
      ...profile,
      createdAt: firestore.serverTimestamp()
    });
  }

  return profile;
}

async function loadProvider(db, user) {
  const { firestore } = await getFirebaseModules();
  const normalizedEmail = (user.email || '').toLowerCase();
  if (!normalizedEmail) return null;

  const providerQuery = firestore.query(
    firestore.collection(db, 'providers'),
    firestore.where('email', '==', normalizedEmail)
  );
  const snapshot = await firestore.getDocs(providerQuery);
  if (snapshot.empty) return null;

  const docSnapshot = snapshot.docs[0];
  const data = docSnapshot.data() || {};
  return {
    ...data,
    id: data.id || docSnapshot.id,
    uid: user.uid,
    email: normalizedEmail,
    role: 'provider'
  };
}

async function setRoleClaim(auth, role) {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication expired. Please sign in again.');

  const token = await user.getIdToken();
  const projectId = window.firebaseConfig?.projectId;
  if (!projectId) throw new Error('Firebase project configuration is missing.');

  const response = await fetch(
    `https://us-central1-${projectId}.cloudfunctions.net/setUserRole`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ uid: user.uid, role })
    }
  );

  if (!response.ok) {
    throw new Error(await response.text() || 'Unable to assign the account role.');
  }

  await user.getIdTokenResult(true);
}

function providerCanSignIn(provider) {
  const status = provider.subscriptionStatus || 'pending';
  if (status === 'approved') return null;
  if (status === 'pending') return 'Your account is pending approval.';
  if (status === 'rejected') return `Your registration was rejected. Reason: ${provider.subscriptionRejectionReason || 'No reason provided'}`;
  if (status === 'suspended') return `Your account is suspended. Reason: ${provider.subscriptionSuspendedReason || 'No reason provided'}`;
  return 'Your account is not active. Please contact Super Admin.';
}

function establishSession(role, profile) {
  if (role === 'client') {
    currentClient = profile;
    currentProvider = null;
    showClientDashboard();
  } else {
    currentProvider = profile;
    currentClient = null;
    showProviderDashboard();
  }

  if (typeof updateHeaderUserBadge === 'function') updateHeaderUserBadge();
}

async function signInWithGoogle(role) {
  let auth = null;
  try {
    const services = await getFirebaseServices();
    auth = services.auth;
    const persistenceCheckboxId = role === 'provider' ? 'providerKeepSignedIn' : 'clientKeepSignedIn';
    const keepSignedIn = Boolean(document.getElementById(persistenceCheckboxId)?.checked);
    await window.setFirebaseAuthPersistence?.(keepSignedIn);
    const { auth: authModule } = await getFirebaseModules();
    const result = await authModule.signInWithPopup(auth, services.provider);
    const user = result.user;

    await assertNotAdmin(user);
    const profile = role === 'client'
      ? await loadOrCreateClient(services.db, user)
      : await loadProvider(services.db, user);

    if (!profile) throw new Error('No provider account found. Please register first.');

    const providerError = role === 'provider' ? providerCanSignIn(profile) : null;
    if (providerError) throw new Error(providerError);

    await setRoleClaim(auth, role);
    establishSession(role, profile);
    notify('Welcome! Signed in with Google.', 'success');
  } catch (error) {
    console.error(`[Firebase OAuth] ${role} Google sign-in failed:`, error);
    if (auth) {
      try { await signOutSafely(auth); } catch (signOutError) {
        console.warn('[Firebase OAuth] Sign-out after failed Google login failed:', signOutError);
      }
    }
    notify(`Google sign-in failed: ${error.message || 'Unknown error'}`, 'error');
  }
}

function bindGoogleButton(id, handler) {
  const button = document.getElementById(id);
  if (!button || button.dataset.firebaseOauthBound === 'true') return;
  button.dataset.firebaseOauthBound = 'true';
  button.addEventListener('click', handler);
}

function initFirebaseOAuth() {
  bindGoogleButton('clientGoogleSignInBtn', () => signInWithGoogle('client'));
  bindGoogleButton('providerGoogleSignInBtn', () => signInWithGoogle('provider'));
}

window.handleClientGoogleSignIn = () => signInWithGoogle('client');
window.handleProviderGoogleSignIn = () => signInWithGoogle('provider');
window.initFirebaseOAuth = initFirebaseOAuth;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFirebaseOAuth, { once: true });
} else {
  initFirebaseOAuth();
}

console.log('[Firebase OAuth] Loaded successfully');
